import Close from '@components/icons/Close/Close'
import {
  getLabel,
  parseQueryAttributeFilters,
  serializeQueryAttributeFilters,
} from '@components/modules/ProductGrid/Filter/filtering'
import ProductCardV2 from '@components/ui/ProductCard/ProductCardV2'
import StockToggle from '@components/ui/Toggle/Toggle'
import { CustomProductGridTile } from '@features/products/api/get-custom-product-grid-tiles'
import { useBarScroll } from '@lib/hooks/productGrid/useBarScroll'
import { useFilterProducts } from '@lib/hooks/productGrid/useFilterProducts'
import { useFindCollections } from '@lib/hooks/productGrid/useFindCollections'
import { useFindDesigners } from '@lib/hooks/productGrid/useFindDesigners'
import { useFindTypes } from '@lib/hooks/productGrid/useFindTypes'
import { queryTypes } from 'next-usequerystate'

import { clientSideSort } from '@lib/client-side-sort'
import {
  SortedCustomProductGridTile,
  useSortedProducts,
} from '@lib/hooks/productGrid/useSortedProducts'
import { isAvailableForPurchase } from '@lib/is-available-for-purchase'
import clsx from 'clsx'
import { parseAsInteger, useQueryState } from 'next-usequerystate'
import { useRouter } from 'next/router'
import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import s from './CustomProductGrid.module.scss'
import FilterDropdown from './Filter/FilterDropdown'
import MobileFilterOverlay from './MobileFilterOverlay/MobileFilterOverlay'
import {
  parseQuerySort,
  serializeQuerySort,
  UrlSorting,
} from './Sorting/sorting'
import SortingDropdown from './Sorting/SortingDropdown'

export interface CustomProductGridProps {
  headline?: string
  products?: CustomProductGridTile[]
  colorSorting: {
    name: string
    colorPicker: string
    backendColor: string
  }[]
  componentPriority?: string[]
  collectionFilter: boolean
  colorSelector: boolean
  debugMode: boolean
  wishlistMode?: boolean
  showVariants?: boolean
}

const CustomProductGrid: FC<CustomProductGridProps> = ({
  headline,
  products: initialProducts = [],
  colorSorting,
  collectionFilter,
  colorSelector,
  debugMode,
  wishlistMode,
  componentPriority,
  showVariants,
}) => {
  const router = useRouter()
  const showProductsOnly = !showVariants
  const [soldOnline, setSoldOnline] = useQueryState(
    'soldOnline',
    queryTypes.boolean.withDefault(router.query.soldOnline === 'true'),
  )
  const [queryFilters, setQueryFilters] = useQueryState('filters', {
    parse: parseQueryAttributeFilters,
    serialize: serializeQueryAttributeFilters,
    defaultValue: [],
  })

  const newColorSorting = useMemo(() => {
    if (!colorSorting) return
    return colorSorting.map((x) => ({
      name: x.name,
      hex: x.colorPicker,
      backendColor: x.backendColor,
    }))
  }, [colorSorting])

  const [gridSize, setGridSize] = useQueryState(
    'gridSize',
    parseAsInteger
      .withOptions({
        history: 'replace',
        shallow: true,
      })
      .withDefault(4),
  )
  const grid = useRef<HTMLUListElement>(null)
  const colorsRef = useRef<HTMLDivElement>(null)

  const { staticProducts, onChangeQueryFilter, removeQueryFilter } =
    useFilterProducts(initialProducts, soldOnline)

  const [sortByQuery, setSortByQuery] = useQueryState('sortBy')
  const [sortBy, setSortBy] = useState<UrlSorting>()

  useEffect(() => {
    setSortBy(parseQuerySort(sortByQuery))
  }, [sortByQuery])

  const childCallbacks = useRef<((e: KeyboardEvent) => void)[]>([])

  // Function for children to register their callback.
  // Returns an unregister function for cleanup.
  const registerChildCallback = useCallback(
    (callback: (e: KeyboardEvent) => void): (() => void) => {
      childCallbacks.current.push(callback)
      // Return an unregister function
      return () => {
        childCallbacks.current = childCallbacks.current.filter(
          (cb) => cb !== callback,
        )
      }
    },
    [],
  )

  useEffect(() => {
    const controller = new AbortController()

    window.addEventListener(
      'keydown',
      (e) => {
        childCallbacks.current.forEach((cb) => cb(e))
      },
      { signal: controller.signal },
    )

    return () => {
      controller.abort()
    }
  }, [])

  const getPrefilteredProducts = useCallback(
    (filtersExcept: string) => {
      return (
        initialProducts &&
        initialProducts.filter((x) => {
          if (
            soldOnline &&
            x.variants.edges.every((v) =>
              isAvailableForPurchase(v.node.availability),
            )
          ) {
            return false
          }
          if (queryFilters.length === 0) return true

          return queryFilters.every((f) => {
            if (f.slug === filtersExcept) return true

            if (
              f.slug === 'collections' ||
              f.slug === 'designers' ||
              f.slug === 'types'
            ) {
              return f.values.some((val) =>
                x.tags.some((tag) => tag.toLowerCase() === val.toLowerCase()),
              )
            }

            return true
          })
        })
      )
    },
    [initialProducts, queryFilters, soldOnline],
  )

  const [moveBarBack] = useBarScroll(colorsRef, queryFilters)

  const [sortColor, setSortColor] = useState(newColorSorting || [])

  // on queryFilter new color, initialColorSorting should be rotated so that the new color is first
  useEffect(() => {
    if (!queryFilters.find((x) => x.slug == 'color'))
      return setSortColor(newColorSorting)
    const index = sortColor.findIndex(
      (x) => x.name === queryFilters.find((x) => x.slug == 'color')?.values[0],
    )
    const rotated = [...sortColor.slice(index), ...sortColor.slice(0, index)]
    setSortColor(rotated)
  }, [queryFilters])

  const { sortedProducts } = useSortedProducts(
    staticProducts ?? [],
    removeQueryFilter,
    sortColor?.map((x) => ({ ...x, hex: x.backendColor })),
    showProductsOnly === undefined ? false : showProductsOnly,
    wishlistMode === undefined ? false : wishlistMode,
    componentPriority,
  )

  const { collections } = useFindCollections(
    getPrefilteredProducts('collections') ?? [],
    showProductsOnly,
  )
  const { designers } = useFindDesigners(
    getPrefilteredProducts('designers') ?? [],
    showProductsOnly,
  )
  const { types } = useFindTypes(
    getPrefilteredProducts('types') ?? [],
    showProductsOnly,
  )

  // get hovered color
  const hoveredColor = useRef<HTMLDivElement>(null)

  const handleHover = (e: any) => {
    const colorName = e.target.getAttribute('data-color-name')

    if (!hoveredColor.current) return
    hoveredColor.current.textContent = colorName
  }

  const finalProducts = useMemo(() => {
    if (!showProductsOnly) return sortedProducts

    const groupedById = sortedProducts.reduce(
      (acc, product) => {
        const id = product.id

        if (!acc[id]) {
          acc[id] = []
        }

        acc[id].push(product)
        return acc
      },
      {} as Record<string, SortedCustomProductGridTile[]>,
    )

    // return first product of each group
    return Object.values(groupedById).map((group) => group[0])
  }, [sortedProducts, showProductsOnly])
  if (!finalProducts) return null
  return (
    <>
      {/* <ThorGrid /> */}
      <div
        className={clsx(s.root, {
          [s.max4]: gridSize >= 4,
          [s.max6]: gridSize >= 6,
        })}
        style={{ '--grid-size': gridSize } as CSSProperties}
      >
        {showProductsOnly && (
          <div className={'showOnMobile'}>
            <div className={s.mobileFilterFlex}>
              <MobileFilterOverlay
                collections={collections}
                types={types}
                designers={designers}
                categories={[]}
                soldOnline={soldOnline}
                onChangeQueryFilter={onChangeQueryFilter}
                queryFilters={queryFilters}
                setSoldOnline={setSoldOnline}
              />
              <SortingDropdown
                optionToggle={(field, direction) => {
                  if (
                    sortBy?.field == field &&
                    sortBy?.direction == direction
                  ) {
                    setSortByQuery(null)
                    return
                  }

                  setSortByQuery(
                    serializeQuerySort(
                      field && direction ? { field, direction } : null,
                    ),
                  )
                }}
                chosen={sortBy}
                isMobile
              />
            </div>
          </div>
        )}
        <div className={s.filters}>
          {headline && <div className={s.headline}>{headline}</div>}
          {colorSelector && (
            <div className={s.colors} ref={colorsRef}>
              <div
                className={clsx(s.hoverBar, {
                  // if query filter is  set, show hover bar
                  [s.visible]: queryFilters.find((x) => x.slug == 'color')
                    ?.values[0],
                })}
              >
                <div className={clsx(s.infoTop, s.infoText)}>Click to view</div>
                <div
                  ref={hoveredColor}
                  className={clsx(s.infoBottom, s.infoText)}
                ></div>
              </div>
              {newColorSorting &&
                newColorSorting.length > 0 &&
                newColorSorting.map((color, index) => {
                  return (
                    <div
                      onMouseEnter={handleHover}
                      data-color-index={index}
                      data-color-name={color.name}
                      key={index}
                      style={{ '--color': color.hex } as CSSProperties}
                      className={clsx('pg-color', s.color, {
                        [s.disabled]: !sortedProducts?.find((product) => {
                          return product.variants?.find(
                            (variant) =>
                              (variant as any)?.colorGroup.name === color.name,
                          )
                        }),
                      })}
                      onTransitionEnd={moveBarBack}
                      onClick={() => {
                        onChangeQueryFilter('color', [
                          {
                            label: color.name,
                            value: color.name,
                          },
                        ])
                      }}
                    ></div>
                  )
                })}
            </div>
          )}
          <div className={s.filterFlex}>
            {!showProductsOnly && collectionFilter && (
              <div className={s.collectionFilter}>
                <FilterDropdown
                  options={collections?.map((tag) => ({
                    label: tag.label,
                    value: tag.value,
                    count: tag.count,
                    disabled: false,
                  }))}
                  selectedOptions={collections?.filter((tag) =>
                    queryFilters.find(
                      (qf) =>
                        qf.slug == 'collections' &&
                        qf.values.includes(tag.value),
                    ),
                  )}
                  placeholder={'COLLECTION'}
                  onChange={(options) =>
                    onChangeQueryFilter('collections', options)
                  }
                />
              </div>
            )}

            {showProductsOnly && (
              <div className={'hideOnMobile'}>
                <div className={s.desktopFilterFlex}>
                  <SortingDropdown
                    optionToggle={(field, direction) => {
                      if (
                        sortBy?.field == field &&
                        sortBy?.direction == direction
                      ) {
                        setSortByQuery(null)
                        return
                      }

                      setSortByQuery(
                        serializeQuerySort(
                          field && direction ? { field, direction } : null,
                        ),
                      )
                    }}
                    chosen={sortBy}
                  />

                  <div className={s.collectionFilter}>
                    <FilterDropdown
                      options={types?.map((tag) => ({
                        label: tag.label,
                        value: tag.value,
                        count: tag.count,
                        disabled: false,
                      }))}
                      selectedOptions={types?.filter((tag) =>
                        queryFilters.find(
                          (qf) =>
                            qf.slug == 'types' && qf.values.includes(tag.value),
                        ),
                      )}
                      placeholder={'TYPE'}
                      onChange={(options) =>
                        onChangeQueryFilter('types', options)
                      }
                    />
                  </div>
                  <div className={s.collectionFilter}>
                    <FilterDropdown
                      options={collections?.map((tag) => ({
                        label: tag.label,
                        value: tag.value,
                        count: tag.count,
                        disabled: false,
                      }))}
                      selectedOptions={collections?.filter((tag) =>
                        queryFilters.find(
                          (qf) =>
                            qf.slug == 'collections' &&
                            qf.values.includes(tag.value),
                        ),
                      )}
                      placeholder={'COLLECTION'}
                      onChange={(options) =>
                        onChangeQueryFilter('collections', options)
                      }
                    />
                  </div>
                  <div className={s.collectionFilter}>
                    <FilterDropdown
                      options={designers?.map((tag) => ({
                        label: tag.label,
                        value: tag.value,
                        count: tag.count,
                        disabled: false,
                      }))}
                      selectedOptions={designers?.filter((tag) =>
                        queryFilters.find(
                          (qf) =>
                            qf.slug == 'designers' &&
                            qf.values.includes(tag.value),
                        ),
                      )}
                      placeholder={'DESIGNER'}
                      onChange={(options) =>
                        onChangeQueryFilter('designers', options)
                      }
                    />
                  </div>

                  <StockToggle
                    enabled={soldOnline}
                    onChange={(value: boolean) => {
                      setSoldOnline(value)
                    }}
                  />
                </div>
              </div>
            )}

            <div className={s.viewBy}>
              View by
              <div
                className={clsx(s.size4selector, {
                  [s.active]: gridSize === 4,
                })}
                onClick={() => setGridSize(4)}
              >
                <span className={s.underlineAnimation}>4</span>
              </div>
              <div
                className={clsx(s.size6selector, {
                  [s.active]: gridSize === 6,
                })}
                onClick={() => setGridSize(6)}
              >
                <span className={s.underlineAnimation}>6</span>
              </div>
              <div
                className={clsx(s.size8selector, {
                  [s.active]: gridSize === 8,
                })}
                onClick={() => setGridSize(8)}
              >
                <span className={s.underlineAnimation}>8</span>
              </div>
            </div>
          </div>

          {queryFilters && queryFilters.length > 0 && (
            <div className={clsx('hideOnMobile')}>
              <div className="flex flex-wrap justify-start mb-6">
                {queryFilters.map((x, index) =>
                  x.values.map((val) => (
                    <div key={index} className="flex mr-2 mb-2">
                      <div
                        className="select-none p-2 text-xs rounded-l-sm"
                        style={{
                          letterSpacing: '0.1rem',
                          backgroundColor: '#f8f7f7',
                        }}
                      >
                        {getLabel(val).replace(/-/gm, ' ')}
                      </div>
                      <button
                        title="Remove"
                        className="select-none p-2 text-xs rounded-r-sm bg-[#f8f7f7] hover:bg-[#eee]"
                        style={{
                          letterSpacing: '0.1rem',
                        }}
                        onClick={() => removeQueryFilter(x.slug, val)}
                      >
                        <Close className="cursor-pointer w-4 h-4" />
                      </button>
                    </div>
                  )),
                )}
              </div>
            </div>
          )}
        </div>

        <ul ref={grid} className={clsx(s.grid)}>
          {wishlistMode
            ? initialProducts?.map((product) => (
                <ProductCardV2
                  wishlistMode
                  debugMode={debugMode}
                  gridSize={gridSize}
                  key={product.id}
                  product={product as any}
                  registerKeyHandler={registerChildCallback} // pass the registration function
                />
              ))
            : finalProducts
                ?.sort((a, b) => clientSideSort(a, b, sortBy))
                // ?.filter((product) => !product.hideAttributes)
                .map((product) =>
                  product.variants.map((variant) => (
                    <ProductCardV2
                      registerKeyHandler={registerChildCallback} // pass the registration function
                      debugMode={debugMode}
                      gridSize={gridSize}
                      key={variant.sku}
                      product={
                        {
                          ...product,
                          heroVariant: variant,
                        } as any
                      }
                    />
                  )),
                )}
        </ul>
      </div>
    </>
  )
}

export default CustomProductGrid
